import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';

/**
 * True once the element has been scrolled into view, and true from the start
 * for anyone who has asked for reduced motion.
 *
 * Shared, because two different things on the landing page need the same
 * answer: a section that fades up as a whole, and a wall of ninety frames that
 * has to arrive one after another.
 */
export function useInView<T extends HTMLElement>(threshold = 0.12) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

/**
 * Fades a section up as it comes into view, once.
 *
 * Scroll animation earns its place here by pacing the page — each section
 * arrives as its own beat rather than the whole story landing at once. It is
 * skipped entirely for anyone who has asked for reduced motion, and the content
 * is always in the DOM, so nothing depends on the animation having run.
 */
export default function Reveal({
  children,
  delay = 0,
  as: Tag = 'div',
  className,
}: {
  children: ReactNode;
  /** Milliseconds, for staggering siblings. Keep under ~200. */
  delay?: number;
  as?: 'div' | 'section' | 'li' | 'article';
  className?: string;
}) {
  const { ref, inView } = useInView<HTMLElement>();

  return (
    <Tag
      ref={ref as never}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={clsx(
        'transition-[opacity,transform] duration-[600ms] ease-out motion-reduce:transition-none',
        inView ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
        className,
      )}
    >
      {children}
    </Tag>
  );
}
